import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import CloseIcon from '@mui/icons-material/Close';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';
import RenderMessage from './messages/RenderMessage';

interface ConversationReplayProps {
  chatId: string;
  open: boolean;
  onClose: () => void;
}

function ConversationReplay({
  chatId,
  open,
  onClose,
}: ConversationReplayProps): React.ReactNode {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSnapshotIndex, setCurrentSnapshotIndex] = useState(0);
  const [timelinePosition, setTimelinePosition] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [displayedMessages, setDisplayedMessages] = useState<any[]>([]);

  // References
  const replayIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Get chat recordings from the store
  const { chatRecordings } = useAlchemystStoreForAi((state) => ({
    chatRecordings: state.chatRecordings,
  }));

  // Chat recording for the current chat
  const recording = chatId ? chatRecordings[chatId] : null;
  const snapshots = recording?.snapshots || [];

  // Handle play/pause for state diff replay
  const togglePlayback = () => {
    if (isPlaying) {
      // Pause playback
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
        replayIntervalRef.current = null;
      }
      setIsPlaying(false);
    } else {
      // Start playback
      if (snapshots.length > 0) {
        replayIntervalRef.current = setInterval(() => {
          setCurrentSnapshotIndex((prev) => {
            const next = prev + 1;
            if (next >= snapshots.length) {
              // End of recording - stop playback
              if (replayIntervalRef.current) {
                clearInterval(replayIntervalRef.current);
                replayIntervalRef.current = null;
              }
              setIsPlaying(false);
              return prev;
            }
            return next;
          });
        }, 2000 / playbackSpeed); // 2 second intervals, adjusted by speed
        setIsPlaying(true);
      }
    }
  };

  // Reset playback
  const resetPlayback = () => {
    if (replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = null;
    }
    setCurrentSnapshotIndex(0);
    setTimelinePosition(0);
    setIsPlaying(false);
    setDisplayedMessages([]);
  };

  // Handle speed change
  const changeSpeed = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);

    // If playback is active, restart it with new speed
    if (isPlaying && replayIntervalRef.current) {
      clearInterval(replayIntervalRef.current);
      replayIntervalRef.current = setInterval(() => {
        setCurrentSnapshotIndex((prev) => {
          const next = prev + 1;
          if (next >= snapshots.length) {
            if (replayIntervalRef.current) {
              clearInterval(replayIntervalRef.current);
              replayIntervalRef.current = null;
            }
            setIsPlaying(false);
            return prev;
          }
          return next;
        });
      }, 2000 / playbackSpeed);
    }
  };

  // Handle timeline slider change
  const handleTimelineChange = (_event: Event, newValue: number | number[]) => {
    const newIndex = typeof newValue === 'number' ? newValue : newValue[0];
    setCurrentSnapshotIndex(newIndex);
    setTimelinePosition(newIndex);
  };

  // Clean up on unmount or when closing
  useEffect(() => {
    return () => {
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
        replayIntervalRef.current = null;
      }
    };
  }, []);

  // Update timeline position and messages when current index changes
  useEffect(() => {
    // Update displayed messages from current snapshot
    const currentSnapshot = snapshots[currentSnapshotIndex];
    if (currentSnapshot?.fullState?.messages) {
      setDisplayedMessages(currentSnapshot.fullState.messages);
      // Scroll to bottom after messages update
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [currentSnapshotIndex, snapshots]);

  // Update timelinePosition when currentSnapshotIndex changes
  useEffect(() => {
    setTimelinePosition(currentSnapshotIndex);
  }, [currentSnapshotIndex]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Conversation Replay
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!recording || snapshots.length === 0 ? (
          <Typography>
            No conversation recording available for this chat session.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              height: '70vh',
            }}
          >
            {/* Messages display */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              <Stack spacing={2}>
                {displayedMessages.map((message, index) => (
                  <Box key={message.id || index} width="100%">
                    <RenderMessage
                      animate={false}
                      {...message}
                      message={message.message.replaceAll('\n', '\n\n')}
                      // json={message.json}
                      // role={message.role}
                      // id=
                    />
                  </Box>
                ))}
                <div ref={chatEndRef} />
              </Stack>
            </Box>

            {/* Playback controls */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <IconButton onClick={togglePlayback}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>

              <IconButton onClick={resetPlayback}>
                <RefreshIcon />
              </IconButton>

              <Slider
                value={timelinePosition}
                onChange={handleTimelineChange}
                min={0}
                max={Math.max(snapshots.length - 1, 0)}
                disabled={snapshots.length <= 1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => {
                  if (snapshots[value]) {
                    return new Date(
                      snapshots[value].timestamp
                    ).toLocaleTimeString();
                  }
                  return '';
                }}
                sx={{ flexGrow: 1 }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() =>
                    changeSpeed(Math.max(0.25, playbackSpeed - 0.25))
                  }
                  disabled={playbackSpeed <= 0.25}
                >
                  <FastRewindIcon />
                </IconButton>

                <Typography
                  variant="body2"
                  sx={{ minWidth: '40px', textAlign: 'center' }}
                >
                  {playbackSpeed}x
                </Typography>

                <IconButton
                  onClick={() => changeSpeed(Math.min(2, playbackSpeed + 0.25))}
                  disabled={playbackSpeed >= 2}
                >
                  <FastForwardIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ConversationReplay;
